"use client";

import { CheckCircle2, CircleAlert, CircleDashed, Clock3 } from "lucide-react";
import { Component, type ReactNode } from "react";

import { formatUSPhoneNumber } from "@/shared/utils/phone-utils";
import { FileViewer } from "@/src/components/file-viewer/file-viewer";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import { useErrorLogs } from "@/src/lib/data-fetchers/error-log-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { buildOutboundFaxFilename, buildOutboundFaxGcsPath } from "@/src/lib/gcs-path";
import { isNotNeededOutput } from "@/src/lib/workflows/steps/not-needed";
import { replyFaxPdfOutputSchema, replyFaxRoutingOutputSchema } from "@/src/models/reply-fax";
import { TaskStatus } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

class FaxPdfPreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? (
      <p role="alert" className="p-4 text-sm">
        The fax PDF preview could not load. Refresh to try again.
      </p>
    ) : (
      this.props.children
    );
  }
}

/** The submitted fax once sent, else the generated preview packet. */
export function outboundFaxPdfPath(taskId: string, taskSteps: TaskStep[]): string | null {
  const generated = taskSteps.find(
    (step) => step.stepKey === "build_reply_fax" && step.status === TaskStepStatus.COMPLETED
  );
  const packet = replyFaxPdfOutputSchema.safeParse(generated?.output);
  const submitted = taskSteps.some(
    (step) => step.status === TaskStepStatus.COMPLETED && step.output?.outcome === "submitted"
  );

  return submitted
    ? buildOutboundFaxGcsPath(taskId, buildOutboundFaxFilename(taskId))
    : packet.success
      ? packet.data.pathToFile
      : null;
}

export function OutboundFaxPdfPreview({ taskId, path }: { taskId: string; path: string }) {
  return (
    <FaxPdfPreviewBoundary key={path}>
      <FileViewer url={fileViewUrl(path)} filename={buildOutboundFaxFilename(taskId)} contentType="application/pdf" />
    </FaxPdfPreviewBoundary>
  );
}

export type DocumentWorkflowStepDefinition = {
  key: string;
  title: string;
  description: string;
};

// An early exit persists as COMPLETED, so a green card would claim work that never landed. These
// outcomes are the evidence that the step did not do its job.
const UNRESOLVED_STEP_OUTCOMES: ReadonlySet<unknown> = new Set(["unmapped_document_type"]);

export function DocumentWorkflowSteps({
  taskId,
  status,
  definitions,
  taskSteps,
}: {
  taskId: string;
  status: TaskStatus;
  definitions: DocumentWorkflowStepDefinition[];
  taskSteps: TaskStep[];
}) {
  const { errorLogs } = useErrorLogs(taskId, { enabled: status === TaskStatus.FAILED });
  const latestError = errorLogs.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.errorMessage;
  const firstUnfinished = definitions.findIndex(
    (definition) => taskSteps.find((step) => step.stepKey === definition.key)?.status !== TaskStepStatus.COMPLETED
  );
  const generated = taskSteps.find(
    (step) => step.stepKey === "build_reply_fax" && step.status === TaskStepStatus.COMPLETED
  );
  const packet = replyFaxPdfOutputSchema.safeParse(generated?.output);
  const pdfPath = outboundFaxPdfPath(taskId, taskSteps);

  return (
    <div className="space-y-4">
      {status === TaskStatus.AWAITING_INFORMATION && (
        <p className="text-sm text-muted-foreground">
          Awaiting an updated document. Fax submission does not confirm delivery.
        </p>
      )}
      {definitions.map((definition, index) => {
        const step = taskSteps.find((candidate) => candidate.stepKey === definition.key);
        const failed = step?.status === TaskStepStatus.FAILED;
        const completed = step?.status === TaskStepStatus.COMPLETED;
        const skipped = completed && (step.output?.outcome === "skipped" || isNotNeededOutput(step.output));
        const unresolved = completed && UNRESOLVED_STEP_OUTCOMES.has(step.output?.outcome);
        const waiting = step?.status === TaskStepStatus.ASYNC || step?.status === TaskStepStatus.PAUSED;
        const running = !step && status === TaskStatus.PROCESSING && index === firstUnfinished;
        const label = failed
          ? "Failed"
          : unresolved
            ? "Needs attention"
            : skipped
              ? "Not needed"
              : completed
                ? step.output?.outcome === "submitted"
                  ? "Submitted"
                  : "Completed"
                : waiting
                  ? "Waiting"
                  : running
                    ? "In progress"
                    : "Not run";
        const routing = replyFaxRoutingOutputSchema.safeParse(step?.output);

        return (
          <ValerieCard
            key={definition.key}
            title={`${index + 1}. ${definition.title}`}
            icon={
              failed || unresolved ? (
                <CircleAlert />
              ) : completed ? (
                <CheckCircle2 />
              ) : waiting || running ? (
                <Clock3 />
              ) : (
                <CircleDashed />
              )
            }
            variant={failed ? "destructive" : completed && !skipped && !unresolved ? "success" : "outline"}
            actions={<Badge variant="outline">{label}</Badge>}
            contentClassName="space-y-2 pt-4"
          >
            <p className="text-sm text-muted-foreground">{definition.description}</p>
            {routing.success && <p className="text-sm">Fax to: {formatUSPhoneNumber(routing.data.targetFaxNumber)}</p>}
            {definition.key === "build_reply_fax" && packet.success && (
              <p className="text-sm">{packet.data.pageCount} pages generated. Preview below.</p>
            )}
            {failed && (
              <p role="alert" className="text-sm text-destructive">
                {latestError || "This step failed. Later steps have not run."}
              </p>
            )}
            {unresolved && (
              <p role="alert" className="text-sm">
                This step did not complete, so later steps have not run. The task note says what is needed.
              </p>
            )}
          </ValerieCard>
        );
      })}
      {definitions.length === 0 && (
        <p className="text-sm text-muted-foreground">No workflow steps are configured for this task.</p>
      )}
      {pdfPath && (
        <ValerieCard title="Fax PDF" contentClassName="h-[900px] p-0">
          <OutboundFaxPdfPreview taskId={taskId} path={pdfPath} />
        </ValerieCard>
      )}
    </div>
  );
}
